// src/utils/uploadImageToImgbb.js
import { httpsCallable } from "firebase/functions";
import { functions as firebaseFunctions } from "../lib/firebase";
import { requestJson } from "../lib/requestClient";
import { errorDev } from './logger';

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_IMGBB_API_URL = "https://api.imgbb.com/1/upload";
const FALLBACK_IMGBB_KEY = "00ebe010975ee15c0a5995ff4efdbad5";

let cachedUploadCallable = null;

function createUploadError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

function getUploadCallable() {
  if (!cachedUploadCallable) {
    cachedUploadCallable = httpsCallable(firebaseFunctions, "uploadImage");
  }
  return cachedUploadCallable;
}

function readEnv(key) {
  if (typeof process !== "undefined" && process?.env?.[key]) {
    return process.env[key];
  }
  if (typeof window !== "undefined") {
    const runtimeEnv = window.__ENV__ || window.__APP_CONFIG__ || {};
    if (runtimeEnv?.[key]) return runtimeEnv[key];
  }
  return undefined;
}

function getImgbbConfig() {
  return {
    apiUrl: readEnv("REACT_APP_IMGBB_API_URL") || DEFAULT_IMGBB_API_URL,
    apiKey:
      readEnv("REACT_APP_IMGBB_KEY") ||
      (typeof window !== "undefined" && window.localStorage?.getItem("REACT_APP_IMGBB_KEY")) ||
      FALLBACK_IMGBB_KEY ||
      "",
  };
}

function getUploadProvider() {
  const provider = String(readEnv("REACT_APP_UPLOAD_PROVIDER") || "imgbb").toLowerCase();
  return provider === "firebase" ? "firebase" : "imgbb";
}

export function getUploadErrorMessage(err) {
  const code = String(err?.code || "");
  const lower = String(err?.message || "").toLowerCase();

  if (code === "INVALID_FILE_TYPE" || code.includes("invalid-argument")) {
    return "Please select a valid image file (JPG, PNG, WEBP, or GIF).";
  }
  if (code === "FILE_TOO_LARGE") {
    return "Image is too large. Please use an image smaller than 10MB.";
  }
  if (code.includes("unauthenticated") || lower.includes("unauthenticated")) {
    return "Please sign in to upload images.";
  }
  return "It's Orrith, not you - please retry your upload.";
}

export async function uploadImageToImgbb(input, options = {}) {
  const { target = "product", retries = DEFAULT_MAX_RETRIES, onProgress } = options;
  const provider = getUploadProvider();
  validateInput(input);

  if (onProgress) onProgress(10);
  const file = await toFile(input);
  const base64 = await toBase64(file);
  if (!base64) {
    throw createUploadError("INVALID_IMAGE", "Could not read image data.");
  }
  if (onProgress) onProgress(35);

  if (provider === "imgbb") {
    const primary = await uploadViaImgbb(file, base64, onProgress);
    if (primary) return primary;
    const firebaseFallback = await uploadViaFirebase(file, base64, target, retries, onProgress);
    if (firebaseFallback) return firebaseFallback;
    throw createUploadError("UPLOAD_FAILED", "Image upload failed on all providers.");
  }

  const firebasePrimary = await uploadViaFirebase(file, base64, target, retries, onProgress);
  if (firebasePrimary) return firebasePrimary;
  const imgbbFallback = await uploadViaImgbb(file, base64, onProgress);
  if (imgbbFallback) return imgbbFallback;

  throw createUploadError("UPLOAD_FAILED", "Image upload failed on all providers.");
}

async function uploadViaFirebase(file, base64, target, retries, onProgress) {
  const callable = getUploadCallable();
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const result = await callable({
        base64,
        fileName: file.name || "image",
        contentType: file.type || "image/jpeg",
        target,
      });
      const url = result?.data?.url || "";
      if (!url) {
        throw createUploadError("SERVICE", "Upload service returned no URL.");
      }
      if (onProgress) onProgress(100);
      return url;
    } catch (err) {
      lastError = err;
      const shouldRetry = attempt < retries && isRetryableUploadError(err);
      if (!shouldRetry) break;
      await delay(350 * Math.pow(2, attempt));
    }
  }

  return null;
}

function isRetryableUploadError(err) {
  const code = String(err?.code || "");
  return (
    code.includes("network") ||
    code.includes("unavailable") ||
    code.includes("deadline") ||
    code.includes("internal")
  );
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeUploadError(err) {
  const code = String(err?.code || "");

  if (code.includes("invalid-argument")) {
    return createUploadError("INVALID_FILE_TYPE", err?.message || "Invalid image payload.");
  }
  if (code.includes("unauthenticated")) {
    return createUploadError("UNAUTHENTICATED", "Please sign in to upload images.");
  }
  if (code.includes("deadline")) {
    return createUploadError("TIMEOUT", "Upload timed out.");
  }
  if (code.includes("unavailable") || code.includes("internal")) {
    return createUploadError("NETWORK", "Upload service unavailable.");
  }
  if (err?.code) {
    return createUploadError(err.code, err.message || "Upload failed.");
  }
  if (err instanceof TypeError) {
    return createUploadError("NETWORK", "Network error while uploading image.");
  }
  return createUploadError("UPLOAD_FAILED", err?.message || "Upload failed.");
}

async function uploadViaImgbb(file, base64, onProgress) {
  const { apiUrl, apiKey } = getImgbbConfig();
  if (!apiKey) return null;

  try {
    if (onProgress) onProgress(55);
    const formData = new FormData();
    formData.append("image", base64);
    formData.append("name", file.name || "image");
    formData.append("key", apiKey);

    const result = await requestJson(apiUrl, {
      method: "POST",
      body: formData,
      timeoutMs: 25_000,
      retries: 1,
      operationName: "uploadImageToImgbb",
    });

    const url = result?.data?.url || result?.data?.display_url || "";
    if (!result?.success || !url) return null;

    if (onProgress) onProgress(100);
    return url;
  } catch (err) {
    errorDev("Operation failed:", err);
    return null;
  }
}

function validateInput(input) {
  if (!input) {
    throw createUploadError("INVALID_IMAGE", "No image provided.");
  }

  if (input instanceof FormData) {
    validateInput(input.get("image"));
    return;
  }

  if (input instanceof File) {
    if (!input.type?.startsWith("image/") || !ALLOWED_TYPES.has(input.type)) {
      throw createUploadError("INVALID_FILE_TYPE", "Only image files are allowed.");
    }
    if (input.size > MAX_IMAGE_SIZE_BYTES) {
      throw createUploadError("FILE_TOO_LARGE", "Image exceeds 10MB size limit.");
    }
    return;
  }

  if (input instanceof Blob) {
    if (input.type && (!input.type.startsWith("image/") || !ALLOWED_TYPES.has(input.type))) {
      throw createUploadError("INVALID_FILE_TYPE", "Only image files are allowed.");
    }
    if (input.size > MAX_IMAGE_SIZE_BYTES) {
      throw createUploadError("FILE_TOO_LARGE", "Image exceeds 10MB size limit.");
    }
    return;
  }

  if (typeof input === "string") {
    return;
  }

  throw createUploadError("INVALID_IMAGE", "Unsupported image input format.");
}

async function toFile(input) {
  if (input instanceof File) return input;

  if (input instanceof FormData) {
    return toFile(input.get("image"));
  }

  if (input instanceof Blob) {
    return new File([input], "image.jpg", { type: input.type || "image/jpeg" });
  }

  if (typeof input === "string") {
    if (input.startsWith("data:")) {
      const blob = await (await fetch(input)).blob();
      return new File([blob], "image.jpg", { type: blob.type || "image/jpeg" });
    }
    throw createUploadError("INVALID_IMAGE", "String URL input is not supported for upload.");
  }

  throw createUploadError("INVALID_IMAGE", "Unsupported image input format.");
}

async function toBase64(input) {
  if (typeof input === "string") {
    if (input.startsWith("data:")) {
      return input.split(",")[1] || "";
    }
    return input;
  }

  if (input instanceof FormData) {
    return toBase64(input.get("image"));
  }

  if (input instanceof Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = String(reader.result || "");
        resolve(value.split(",")[1] || "");
      };
      reader.onerror = () => reject(createUploadError("INVALID_IMAGE", "Could not read image file."));
      reader.readAsDataURL(input);
    });
  }

  throw createUploadError("INVALID_IMAGE", "Unsupported image input format.");
}